/**
 * AuthErrorFallback — ce que voit l'utilisateur quand une page d'auth plante.
 *
 * @remarks
 * La zone auth n'avait aucun `errorElement` : la moindre erreur de rendu, dont
 * un fichier de vue introuvable après un redéploiement, remontait jusqu'au
 * `RootErrorBoundary` — une page d'erreur plein écran, hors du cadre de marque,
 * qu'aucune navigation ne débloque (SAAS-984).
 *
 * Ce fallback reste dans l'`AuthLayout` et se lève dès que l'utilisateur
 * navigue ailleurs : React Router efface l'erreur à chaque navigation.
 *
 * Il ne réutilise pas `AuthCard` / `AuthButton` : le shell n'importe pas les
 * composants d'une feature. Les classes reprennent leur grammaire.
 */

import { Link, useRouteError } from 'react-router-dom';
import { RotateCcw, TriangleAlert } from 'lucide-react';
import { AltLogo } from '@/shared/components';
import { AuthButton } from '@/features/auth/components/AuthButton';
import { Heading } from '@/shared/ui';
import { isChunkLoadError, reloadPage } from './chunk-load-error';

export function AuthErrorFallback() {
  const error = useRouteError();

  console.error('[AuthErrorFallback]', error);

  const staleVersion = isChunkLoadError(error);
  const detail = error instanceof Error ? error.message : null;

  return (
    <div className="relative z-10 w-full max-w-[440px]">
      <div className="mb-7 flex flex-col items-center gap-4">
        <AltLogo className="h-10 w-auto" forceTheme="dark" />
      </div>

      <div className="border-border bg-bg-subtle rounded-xl border px-9 pt-10 pb-8">
        <div role="alert" className="flex flex-col items-center gap-5 text-center">
          <div className="bg-brand/10 flex size-12 items-center justify-center rounded-full">
            <TriangleAlert className="text-brand-light size-6" aria-hidden />
          </div>

          <div>
            <Heading level="page" className="font-display text-2xl">
              {staleVersion
                ? 'Une nouvelle version est disponible'
                : "Cette page n'a pas pu s'afficher"}
            </Heading>
            <p className="text-fg-secondary mt-2 text-sm">
              {staleVersion
                ? 'Le site a été mis à jour pendant votre visite. Rechargez la page pour continuer.'
                : "Un problème est survenu à l'affichage. Rechargez la page pour réessayer."}
            </p>
          </div>

          <AuthButton type="button" onClick={reloadPage}>
            <RotateCcw className="size-4" aria-hidden />
            Recharger la page
          </AuthButton>

          <Link to="/login" className="text-fg-muted text-xs hover:underline">
            Retour à la connexion
          </Link>

          {detail !== null && (
            <details className="text-fg-muted w-full text-left text-xs">
              <summary className="cursor-pointer">Détails techniques</summary>
              <pre className="bg-bg-muted mt-2 overflow-auto rounded-md p-3 whitespace-pre-wrap">
                {detail}
              </pre>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}
